import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { MessageCircle, RefreshCw, CheckCircle2, XCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function WhatsApp() {
  const [status, setStatus] = useState(null);
  const [groups, setGroups] = useState([]);
  const [cfg, setCfg] = useState({ enabled: false, vip_group: "", free_group: "" });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await client.get("/whatsapp/status");
      setStatus(data);
      setCfg(data.config);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/whatsapp/groups");
      setGroups(data.groups || []);
      if (!data.groups?.length) toast.info("No groups found. Add your WhatsApp number to the groups first.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to fetch groups"); }
    setLoading(false);
  };

  const save = async () => {
    setBusy(true);
    try { await client.post("/whatsapp/config", cfg); toast.success("WhatsApp settings saved"); loadStatus(); }
    catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const test = async (group) => {
    try { await client.post("/whatsapp/test", { group }); toast.success(`Test sent to ${group} group! Check WhatsApp 📲`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const connected = status?.connected;
  const configured = status?.configured;

  return (
    <Layout title="WhatsApp Signals">
      <div className="max-w-2xl space-y-5">
        {/* Connection status */}
        <div className="surface p-5" data-testid="wa-status-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-black"><MessageCircle size={20} /></div>
            <div>
              <div className="font-head text-lg text-white">WhatsApp Gateway</div>
              <div className="text-xs text-slate-400">VIP & Free signals auto-forwarded to WhatsApp groups (via Whapi.Cloud)</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm" data-testid="wa-connection-state">
            {!configured ? (
              <span className="flex items-center gap-1.5 text-amber-400"><XCircle size={15} />Not connected — WHAPI_TOKEN missing. Add the token & restart backend.</span>
            ) : connected ? (
              <span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={15} />Connected · {status.groups_count} groups available</span>
            ) : (
              <span className="flex items-center gap-1.5 text-red-400"><XCircle size={15} />Token set but channel offline — re-link your WhatsApp number (QR) on Whapi dashboard.</span>
            )}
          </div>
        </div>

        {/* Group mapping */}
        <div className="surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-head text-base text-white">Map your groups</div>
            <button onClick={loadGroups} disabled={!configured || loading} data-testid="wa-refresh-groups" className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors duration-200 disabled:opacity-50">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}Fetch groups
            </button>
          </div>

          {[["vip_group", "VIP group (full signal + SL)"], ["free_group", "Free group (SL stripped)"]].map(([key, label]) => (
            <div key={key} className="mb-4">
              <label className="text-xs uppercase tracking-wider text-slate-500">{label}</label>
              <div className="flex gap-2 mt-1">
                {groups.length > 0 ? (
                  <select data-testid={`wa-select-${key}`} value={cfg[key] || ""} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                    <option value="">— select group —</option>
                    {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                ) : (
                  <input data-testid={`wa-input-${key}`} value={cfg[key] || ""} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} placeholder="1203630xxxx@g.us" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none" />
                )}
                <button onClick={() => test(key === "vip_group" ? "vip" : "free")} disabled={!cfg[key]} data-testid={`wa-test-${key}`} className="px-3 py-2 text-xs flex items-center gap-1 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors duration-200 disabled:opacity-40"><Send size={12} />Test</button>
              </div>
            </div>
          ))}

          <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="wa-enable-toggle">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-[#25D366] w-4 h-4" />
            <span className="text-sm text-slate-200">Auto-forward signals to WhatsApp (VIP + Free)</span>
          </label>

          <div className="mt-5 pt-4 border-t border-[color:var(--border-subtle)] space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Telegram Free-channel buttons</div>
            <div>
              <label className="text-xs text-slate-400">"Join WhatsApp Community" link</label>
              <input data-testid="wa-community-link" value={cfg.community_link || ""} onChange={(e) => setCfg({ ...cfg, community_link: e.target.value })} placeholder="https://chat.whatsapp.com/xxxx" className="w-full mt-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400">"Chat us on WhatsApp" number (with country code, no +)</label>
              <input data-testid="wa-chat-number" value={cfg.chat_number || ""} onChange={(e) => setCfg({ ...cfg, chat_number: e.target.value })} placeholder="971544289986" className="w-full mt-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none" />
            </div>
          </div>

          <button onClick={save} disabled={busy} data-testid="wa-save" className="btn-gold w-full mt-4 py-2 text-sm">{busy ? "Saving…" : "Save settings"}</button>
        </div>

        <div className="text-xs text-slate-500 leading-relaxed">
          <b className="text-slate-400">Setup:</b> Sign up at whapi.cloud → create a channel → scan the QR with your WhatsApp number → paste the API token into the backend <span className="font-mono">WHAPI_TOKEN</span> env. Add that same number to both WhatsApp groups, then hit "Fetch groups" and map them above.
          <br /><span className="text-amber-500/80">⚠️ Uses an unofficial WhatsApp Web session — use a spare number to avoid any ban risk on your main number.</span>
        </div>
      </div>
    </Layout>
  );
}
